/**
 * useChatStream.types — the per-turn mutable context + the stable dependency
 * bundle shared by the two deferred cores extracted from useChat.sendMessage.
 *
 * PHASE 2 of the useChat decomposition. The SSE `for await (...openChatStream...)`
 * loop (core A → runChatStream) and the ui-action `report.*` / `html.*` switch
 * (core B → applyUiDirective) were previously inline in sendMessage and shared
 * 16 reassigned turn-local primitives + 2 Maps, all created fresh per send and
 * reassigned across ~1200 lines. To lift them out WITHOUT copying values across
 * a boundary (which would silently fork the mutation chain and change behavior),
 * we bundle every reassigned turn-local into a single mutable {@link TurnContext}
 * object and pass it BY REFERENCE. Every `ctx.<field> = …` write is observed by
 * sendMessage's catch/finally and by the other core on the next iteration —
 * exactly as the inline closure variables were.
 *
 * Behavior is pinned by the characterization net:
 *   - useChat.stream-loop-char.test.ts (loop ordering + turn-local transitions)
 *   - useChat.ui-directive-char.test.ts (report/html switch)
 *   - useChat.finalize-char.test.ts (finalizeAssistantMessage assembly)
 *   - useChat.html-stream-char.test.ts / useChat.error-abort-char.test.ts
 */

import type { QueryClient } from '@tanstack/react-query'
import type { ChatRequest, ChatUIAction } from '@/lib/api/endpoints/chat'
import type { ReportConfig } from '@/types/report'
import type { ChatMessage, QuickAction, StreamActivityStep } from './useChat.types'
import type { PendingCreateRegistry } from '@/features/chat/utils/pendingCreateRegistry'

/** Per-turn active report module being built during this stream.
 *
 * The `[key: string]: unknown` index signature mirrors the structural shape of
 * the inline `Map<string, { moduleId; config; focused }>` literal the loop used
 * pre-decomposition. Without it, this NAMED interface is rejected where the
 * inline literal type was accepted (e.g. `findDuplicateReportId`'s
 * `ReadonlyMap<string, ActiveReportEntry>` parameter, whose entry carries its own
 * index signature). Keeping it preserves the inline call's exact typing — no new
 * strict error category is introduced by the relocation. */
export interface ActiveReportModuleEntry {
  moduleId: string
  config: ReportConfig
  focused: boolean
  [key: string]: unknown
}

/** Per-turn active html_viewer module being streamed during this response. */
export interface ActiveHtmlModuleEntry {
  moduleId: string
  html: string
  headComplete: boolean
  previewStarted: boolean
  focused: boolean
  /**
   * Id of the parent Pages container module the html_viewer row was created
   * under, resolved from the modules cache at html.init. Carried on the
   * entry so the html.chunk first-body handler can auto-select the building
   * page inside the Pages pane and html.complete can invalidate the exact
   * page-object list that feeds the Pages rows. Absent when no Pages module
   * was cached at create time (and for edit-of-current-page reuse, where the
   * page is already open).
   */
  pagesParentId?: string
}

/**
 * Mutable per-turn context. Created fresh per sendMessage by
 * {@link createTurnContext} and passed BY REFERENCE to runChatStream,
 * finalizeAssistantMessage, and applyUiDirective. NEVER destructured or copied
 * field-by-field — every read/write must go through `ctx.<field>` so the single
 * source of truth is preserved across the cores + the catch/finally.
 *
 * Holds exactly the 16 reassigned turn-local primitives + the 2 Maps that the
 * inline cores shared (useChat.ts L667-L718, pre-decomposition).
 */
export interface TurnContext {
  // ── Accumulated assistant reply + attribution ─────────────────────────────
  assistantContent: string
  toolsUsedCount: number
  routingInfo: string
  modelInfo: string
  responseDurationMs: number
  // ── Receipt / finalize bookkeeping ────────────────────────────────────────
  sawReceipt: boolean
  messageFinalized: boolean
  createOrdinal: number
  requestAbortController: AbortController | null
  // ── Paragraph-break + status-pacing flags ─────────────────────────────────
  sawInterleavedActivitySinceLastText: boolean
  sawXaviorStatusSinceLastText: boolean
  lastXaviorStatusAtMs: number
  // ── html.init buffering + silent-stop detection ───────────────────────────
  htmlInitPending: boolean
  htmlPendingBuffer: string
  sawHtmlStatusMessage: boolean
  htmlModuleEverCreated: boolean
  // ── Per-turn module registries (the 2 Maps) ───────────────────────────────
  activeReportModules: Map<string, ActiveReportModuleEntry>
  activeHtmlModules: Map<string, ActiveHtmlModuleEntry>
}

/**
 * Build a fresh per-turn context. Mirrors the inline initializers verbatim
 * (useChat.ts L667-L718): all primitives start at their inline defaults and the
 * 2 Maps start empty. Call once per sendMessage; pass the returned object by
 * reference everywhere.
 */
export function createTurnContext(): TurnContext {
  return {
    assistantContent: '',
    toolsUsedCount: 0,
    routingInfo: '',
    modelInfo: '',
    responseDurationMs: 0,
    sawReceipt: false,
    messageFinalized: false,
    createOrdinal: 0,
    requestAbortController: null,
    sawInterleavedActivitySinceLastText: false,
    sawXaviorStatusSinceLastText: false,
    lastXaviorStatusAtMs: 0,
    htmlInitPending: false,
    htmlPendingBuffer: '',
    sawHtmlStatusMessage: false,
    htmlModuleEverCreated: false,
    activeReportModules: new Map<string, ActiveReportModuleEntry>(),
    activeHtmlModules: new Map<string, ActiveHtmlModuleEntry>(),
  }
}

/** Type of the active-page-context value carried on a ref. */
type ActivePageContextValue =
  | {
      rawId?: string
      pageId?: string
      title?: string
      [key: string]: unknown
    }
  | null
  | undefined

/**
 * Stable, turn-spanning dependencies the two cores read but never reassign on
 * `ctx`. Bundled into one object so the core signatures stay
 * `runChatStream(ctx, deps)` / `applyUiDirective(ctx, action, deps)`.
 *
 * Members fall into four groups:
 *   1. turn-stable values created by sendMessage for THIS turn (content,
 *      request, assistantId, the queued-action arrays, etc.) — these are shared
 *      mutable containers (arrays/registry) the cores push into;
 *   2. React state setters + activity-timeline helpers (stable identities);
 *   3. refs (stable identities) the cores read `.current` from;
 *   4. props / hook options / queryClient.
 *
 * `markInterleavedActivity` / `markXaviorStatusActivity` close over `ctx` and
 * are constructed in runChatStream; applyUiDirective receives them via deps so
 * the report/html branches mutate the SAME ctx flags as the loop body.
 */
export interface ChatStreamDeps {
  // ── Turn-stable values (this send) ────────────────────────────────────────
  content: string
  request: ChatRequest
  targetConversationId: string | undefined
  requestStartTime: number
  assistantId: string
  assistantThinking: string
  hideAssistantResponse: boolean
  toolCalls: Array<{ name: string; params: unknown }>
  toolResults: Array<Record<string, unknown>>
  queuedUiActions: ChatUIAction[]
  queuedQuickActions: QuickAction[]
  pendingCreates: PendingCreateRegistry
  isTargetConversationActive: () => boolean
  /** Mark interleaved (tool/status) activity since the last visible text. */
  markInterleavedActivity: () => void
  /** Mark a XAVIOR status message since the last visible text. */
  markXaviorStatusActivity: () => void

  // ── Props / options / infra ───────────────────────────────────────────────
  token: string | null
  workspaceId: string
  // Matches UseChatOptions.mode — finalizeAssistantMessage writes it onto the
  // ChatMessage, whose `mode` field is the same narrow union.
  mode: 'local' | 'api' | undefined
  queryClient: QueryClient
  activePageContext: ActivePageContextValue

  // ── Refs (read `.current`) ────────────────────────────────────────────────
  activePageContextRef: { current: ActivePageContextValue }
  // `onboardingState` is read only as the arg to isOnboardingComplete(state:
  // string | null | undefined) in finalizeAssistantMessage's onboarding chip
  // gate — so it carries that exact param type (matches the inline closure).
  profileRef: { current: { onboardingState?: string | null } | null | undefined }
  focusedSectionsRef: {
    current: Array<{ sectionId: string; label: string; selector?: string }>
  }
  lastSentContentRef: { current: string | null }
  lastSendRef: { current: { content: string; at: number } | null }
  lastStreamEventAtRef: { current: number }
  abortControllerRef: { current: AbortController | null }
  streamActivityRef: { current: Array<{ type: string; label: string }> }
  streamingContentRef: { current: string }
  /** In-flight dedup guard, released once the stream starts (useChat.ts L864). */
  sendInFlightRef: { current: boolean }

  // ── State setters ─────────────────────────────────────────────────────────
  setThinking: (v: string | null) => void
  setIsStreaming: (v: boolean) => void
  setIsLoading: (v: boolean) => void
  setMessages: (updater: (prev: ChatMessage[]) => ChatMessage[]) => void
  setStreamingMessage: (v: ChatMessage | null) => void
  setError: (v: Error | null) => void
  setRetryBubble: (v: ChatMessage | null) => void
  setPendingQuickActions: (v: QuickAction[]) => void
  setFocusedSections: (
    v: Array<{ sectionId: string; label: string; selector?: string }>,
  ) => void

  // ── Activity-timeline helpers ─────────────────────────────────────────────
  pushActivity: (label: string, type: StreamActivityStep['type']) => void
  pushThinkingStatus: (label: string) => void
  clearActivity: () => void

  // ── Caller callbacks (chat options) ───────────────────────────────────────
  onMessage?: (m: ChatMessage) => void
  onQuickAck?: (text: string) => void
  onToolActivity?: () => void
  onObjectCreated?: (id: string) => void
  onStreamTextChunk?: (chunk: string) => void
  onError?: (e: Error) => void
  onComplete?: () => void
  onUiActions?: (actions: ChatUIAction[]) => void
}
